import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import selectors from 'selectors/selectors';
import { getTodosNotificationSlider } from 'screens/todos/store/todosScreenActions';
import { action } from 'lib/actionHelpers.js';
import { differenceInMinutes } from 'date-fns';
import { formatDate } from 'lib/dateUtilities';
import NotificationSliderContent from 'components/notificationSlider/NotificationSliderContent.js';
import { useEffectBlockFirstLoad } from 'hooks/index.js';
import { getInboxMessages } from 'screens/inbox/store/inboxScreenActions.js';
import inboxFoldersSelector from 'screens/inbox/store/inboxFoldersSelector.js';
import setQuery from 'lib/setQuery.js';
import inboxFolderSelector from 'screens/inbox/store/inboxFolderSelector.js';
import { withRouter } from 'react-router';

const getNewlyDueTodos = (dueTodos) => {
  return dueTodos.filter((item) => {
    const formattedDate = formatDate(item.target_datetime);
    const diff = differenceInMinutes(new Date(formattedDate), new Date());
    return diff === 14;
  });
};

const NotificationSlider = (props) => {
  const notificationFolderId = useSelector(selectors.notificationFolderId);
  const folderId = useSelector(inboxFolderSelector);
  const dueTodos = useSelector(selectors.notificationSidebarTodosDueToday);
  const notificationSliderOpen = useSelector(selectors.notificationSliderOpen);
  const folders = useSelector(inboxFoldersSelector);
  const folderOffset = useSelector(selectors.folderOffset(folderId));
  const notificationFolderOffset = useSelector(selectors.folderOffset(notificationFolderId));

  const serviceProviderId = useMemo(() => {
    return props.location.pathname.split('/')[1];
  }, [props.location.pathname]);

  const visibleFolders = folders.filter((n) => (n.visibility || []).includes('notificationsSlider'));
  const folderMethods = Object.fromEntries(visibleFolders.map((n) => [n.id, () => getInboxMessages({ folderId: notificationFolderId })]));
  const methodMap = {
    ...folderMethods,
    todosOverdue: () => getTodosNotificationSlider({ serviceProviderId }),
    todos: () => getTodosNotificationSlider({ serviceProviderId }),
  };

  const getActiveTabData = (tab) => {
    if (methodMap[tab || notificationFolderId]) {
      methodMap[tab || notificationFolderId]();
    }
  };

  const handleTabClick = (e, tab) => {
    getActiveTabData(tab);
  };

  useEffect(() => {
    const defaultFolderId = visibleFolders[0].id;
    if (notificationSliderOpen && !notificationFolderId && defaultFolderId) {
      setQuery({ notificationFolderId: defaultFolderId });
    }
  }, [notificationFolderId, notificationSliderOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newlyDueTodos = getNewlyDueTodos(dueTodos);
      if ((newlyDueTodos || []).length) {
        newlyDueTodos.forEach((todo) => {
          const eventPayload = {
            fixture: true,
            event: {
              service_provider_id: todo.service_provider_id,
              type: 'updated_todo',
              todo: { service_provider_id: todo.service_provider_id, type: 'updated_todo', event_data: { id: todo.id, service_provider_id: todo.service_provider_id, todo: { ...todo } } },
            },
          };
          action('GET_EVENT', eventPayload);
          action('GET_UPDATED_TODO_EVENT', eventPayload);
        });
      }
    }, 45000);
    return () => clearInterval(interval);
  }, [dueTodos]);

  useEffect(() => {
    getTodosNotificationSlider({ serviceProviderId });
    if (notificationSliderOpen) {
      visibleFolders.forEach((n) => {
        getInboxMessages({ folderId: n.id });
      });
    }
  }, [serviceProviderId, notificationSliderOpen]);

  useEffectBlockFirstLoad(() => {
    if (notificationSliderOpen) {
      getActiveTabData();
    }
  }, [notificationFolderId, notificationSliderOpen]);

  useEffectBlockFirstLoad(() => getInboxMessages({ folderId }), [folderId, folderOffset]);
  useEffectBlockFirstLoad(() => getInboxMessages({ folderId: notificationFolderId }), [notificationFolderId, notificationFolderOffset]);

  if (!notificationSliderOpen) {
    return null;
  }

  return <NotificationSliderContent onTabClick={handleTabClick} />;
};

export default withRouter(NotificationSlider);
